import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, FileText, DollarSign, MapPin } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { COMPANY_INFO } from '../data/siteData';

import { API } from '../config/api';

export default function PayInvoice() {
  const [address, setAddress] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!address.trim() || address.trim().length < 3) {
      setError('Please enter at least 3 characters of your address.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.get(`${API}/invoices/search`, { params: { address: address.trim() } });
      setResults(res.data);
    } catch (err) {
      setError('Failed to search. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) => {
    if (status === 'paid') return 'bg-green-100 text-green-700';
    if (status === 'overdue') return 'bg-red-100 text-red-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div data-testid="pay-invoice-page">
      {/* Hero */}
      <section className="bg-sky-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700 mb-3">Invoice Payment</p>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-black text-slate-900 mb-4">
            Pay Your Invoice
          </h1>
          <p className="text-slate-500 text-base max-w-lg mx-auto">
            Search for your invoice using your service address to view and pay online.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch} className="flex gap-3 mb-8" data-testid="invoice-search-form">
            <div className="flex-1 relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                data-testid="invoice-address-input"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Enter your service address..."
                className="pl-10"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="invoice-search-btn"
              className="bg-sky-700 text-white rounded-full px-6 hover:bg-sky-600"
            >
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </form>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm rounded-xl px-4 py-3 mb-6" data-testid="invoice-search-error">
              {error}
            </div>
          )}

          {/* Results */}
          {results !== null && (
            <div data-testid="invoice-search-results">
              {results.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-sky-200 mx-auto mb-4" />
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">No Invoices Found</h3>
                  <p className="text-slate-400 text-sm">
                    No invoices match that address. Please check your address or contact us at{' '}
                    <a href={`tel:${COMPANY_INFO.phone}`} className="text-sky-700 font-medium">{COMPANY_INFO.phone}</a>.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-slate-500 text-sm mb-2">{results.length} invoice(s) found</p>
                  {results.map((inv) => (
                    <div
                      key={inv.id}
                      className="bg-white rounded-2xl border border-sky-100 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/pay/invoice/${inv.id}`)}
                      data-testid={`invoice-result-${inv.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
                            <FileText className="w-5 h-5 text-sky-700" />
                          </div>
                          <div>
                            <p className="font-heading font-semibold text-slate-900">{inv.invoice_number}</p>
                            <p className="text-xs text-slate-400">{inv.customer_name}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-heading font-bold text-lg text-slate-900">${inv.total.toFixed(2)}</p>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColor(inv.status)}`}>
                            {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                          </span>
                        </div>
                      </div>
                      <p className="text-slate-400 text-xs">{inv.address}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
